<template>
  <div class="w-full">
    <div
      ref="track"
      class="relative h-16 overflow-hidden rounded-full bg-primary select-none"
      :class="disabled ? 'opacity-50' : 'selfscan-slider-track'"
    >
      <div
        class="absolute inset-y-0 left-0 bg-primary transition-[width] duration-75"
        :style="{ width: `${progressPercent}%` }"
        aria-hidden="true"
      />
      <div
        v-if="!disabled && !busy && !completed"
        class="selfscan-slider-shimmer pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-primary-text/30 to-transparent"
        aria-hidden="true"
      />
      <p
        class="pointer-events-none absolute inset-0 flex items-center justify-center px-16 text-center text-sm font-semibold text-primary-text"
      >
        {{ busy ? $t('selfscan.activating') : $t('selfscan.swipe_instruction') }}
      </p>
      <button
        ref="thumb"
        type="button"
        role="slider"
        class="absolute left-1 top-1 flex h-14 w-14 touch-none items-center justify-center rounded-full border border-surface-grey bg-surface-light text-headings shadow transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :class="dragging ? 'cursor-grabbing duration-0' : 'cursor-grab duration-300'"
        :style="{ transform: `translateX(${position}px)` }"
        :disabled="disabled || busy || completed"
        :aria-label="$t('selfscan.swipe_accessible_label')"
        :aria-valuemin="0"
        :aria-valuemax="100"
        :aria-valuenow="Math.round(progressPercent)"
        :aria-valuetext="$t('selfscan.swipe_progress', { progress: Math.round(progressPercent) })"
        :aria-busy="busy || undefined"
        @pointerdown="startDrag"
        @pointermove="moveDrag"
        @pointerup="finishDrag"
        @pointercancel="cancelDrag"
        @keydown.enter.prevent="completeWithKeyboard"
        @keydown.space.prevent="completeWithKeyboard"
      >
        <CheckmarkIcon v-if="completed || busy" class="h-4 w-5 fill-current" />
        <ArrowRightIcon v-else class="h-5 w-5 fill-current" />
      </button>
    </div>
    <p class="mt-2 hidden text-center text-xs text-text sm:block">
      {{ $t('selfscan.swipe_keyboard_instruction') }}
    </p>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
  import CheckmarkIcon from '@/components/icons/CheckmarkIcon.vue';

  const props = withDefaults(
    defineProps<{
      disabled?: boolean;
      busy?: boolean;
      resetKey?: number;
    }>(),
    {
      disabled: false,
      busy: false,
      resetKey: 0,
    }
  );

  const emit = defineEmits<{
    complete: [];
  }>();

  const track = ref<HTMLElement | null>(null);
  const thumb = ref<HTMLButtonElement | null>(null);
  const position = ref(0);
  const maxPosition = ref(0);
  const dragging = ref(false);
  const completed = ref(false);
  let activePointerId: number | null = null;
  let dragStartX = 0;
  let dragStartPosition = 0;

  const progressPercent = computed(() =>
    maxPosition.value > 0 ? Math.min((position.value / maxPosition.value) * 100, 100) : 0
  );

  onMounted(() => {
    measure();
    window.addEventListener('resize', handleResize);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
  });

  watch(
    () => props.resetKey,
    async () => {
      reset();
      await nextTick();
      measure();
    }
  );

  function measure() {
    if (!track.value || !thumb.value) return;
    const trackWidth = track.value.getBoundingClientRect().width;
    const thumbWidth = thumb.value.getBoundingClientRect().width;
    maxPosition.value = Math.max(trackWidth - thumbWidth - 8, 0);
    position.value = completed.value ? maxPosition.value : Math.min(position.value, maxPosition.value);
  }

  function startDrag(event: PointerEvent) {
    if (props.disabled || props.busy || completed.value || event.button !== 0) return;
    measure();
    activePointerId = event.pointerId;
    dragStartX = event.clientX;
    dragStartPosition = position.value;
    dragging.value = true;
    thumb.value?.setPointerCapture(event.pointerId);
  }

  function moveDrag(event: PointerEvent) {
    if (!dragging.value || event.pointerId !== activePointerId) return;
    position.value = clamp(dragStartPosition + event.clientX - dragStartX, 0, maxPosition.value);
  }

  function finishDrag(event: PointerEvent) {
    if (!dragging.value || event.pointerId !== activePointerId) return;
    releasePointer(event.pointerId);
    if (progressPercent.value >= 95) {
      complete();
    } else {
      position.value = 0;
    }
  }

  function cancelDrag(event: PointerEvent) {
    if (event.pointerId !== activePointerId) return;
    releasePointer(event.pointerId);
    position.value = 0;
  }

  function releasePointer(pointerId: number) {
    if (thumb.value?.hasPointerCapture(pointerId)) {
      thumb.value.releasePointerCapture(pointerId);
    }
    dragging.value = false;
    activePointerId = null;
  }

  function completeWithKeyboard() {
    if (props.disabled || props.busy || completed.value) return;
    measure();
    complete();
  }

  function complete() {
    if (completed.value) return;
    completed.value = true;
    position.value = maxPosition.value;
    emit('complete');
  }

  function reset() {
    if (activePointerId !== null) releasePointer(activePointerId);
    completed.value = false;
    position.value = 0;
  }

  function handleResize() {
    if (dragging.value) {
      reset();
    }
    measure();
  }

  function clamp(value: number, minimum: number, maximum: number) {
    return Math.min(Math.max(value, minimum), maximum);
  }
</script>

<style scoped>
  .selfscan-slider-track {
    touch-action: pan-y;
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 30%, transparent);
    animation: selfscan-slider-glow 2s ease-in-out infinite;
  }

  .selfscan-slider-shimmer {
    animation: selfscan-slider-shimmer 2s ease-in-out infinite;
  }

  @keyframes selfscan-slider-glow {
    0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 30%, transparent); }
    50% { box-shadow: 0 0 0.75rem 0.125rem color-mix(in srgb, var(--color-primary) 35%, transparent); }
  }

  @keyframes selfscan-slider-shimmer {
    0% { transform: translateX(0); }
    100% { transform: translateX(300%); }
  }

  @media (prefers-reduced-motion: reduce) {
    .selfscan-slider-track *,
    .selfscan-slider-track *::before,
    .selfscan-slider-track *::after {
      transition-duration: 0ms !important;
    }
  }
</style>
